import FormField from '@/components/ui/form-field';
import type { FormSpacing } from '@/theme/size-tokens';

import TagInput, { type TagInputProps } from './tag-input';

export type FormTagInputProps = Omit<TagInputProps, 'validationState'> & {
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Spacing between label, input, and helper text */
  spacing?: FormSpacing;
  /** Error message – also sets validation state to error */
  error?: string;
  /** Reserve space for error message to prevent layout shift */
  reserveMessageSpace?: boolean;
};

/**
 * FormTagInput – pill/tag entry field wrapped with FormField for label & error display.
 *
 * Press Enter or type a comma to commit the current draft as a tag.
 * Press Backspace on an empty draft to remove the last tag.
 * Tags are auto-sanitized: lowercased, spaces/underscores converted to "-",
 * and non-[a-z0-9-] characters stripped.
 *
 * @example
 * ```tsx
 * const [tags, setTags] = useState<string[]>([]);
 * <FormTagInput label="Requirements" value={tags} onChange={setTags} />
 * ```
 */
function FormTagInput({
  label,
  helperText,
  spacing,
  error,
  reserveMessageSpace,
  disabled,
  id,
  ...tagInputProps
}: Readonly<FormTagInputProps>) {
  return (
    <FormField
      label={label}
      helperText={helperText}
      spacing={spacing}
      error={error}
      disabled={disabled}
      id={id}
      reserveMessageSpace={reserveMessageSpace}
    >
      <TagInput
        validationState={error ? 'error' : 'neutral'}
        disabled={disabled}
        {...tagInputProps}
      />
    </FormField>
  );
}

FormTagInput.displayName = 'FormTagInput';

export default FormTagInput;
